import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { clinicIdSchema } from './clinic'

// ---------------------------------------------------------------------------
// Consultation status — derived from timestamps, not stored
// ---------------------------------------------------------------------------

export const consultationStatusSchema = z.enum(['pending', 'in_progress', 'finalised'])
export type ConsultationStatus = z.infer<typeof consultationStatusSchema>

// ---------------------------------------------------------------------------
// Consultation type
// ---------------------------------------------------------------------------

/** Whether the consultation is conducted in-person or remotely. */
export type ConsultationType = 'clinic' | 'remote'

// ---------------------------------------------------------------------------
// Base types
// ---------------------------------------------------------------------------

export const consultationSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  appointmentId: z.uuid().nullable(),
  clinicId: clinicIdSchema.nullable(),
  /** The lead vet or nurse. Null for unassigned remote (pending) consultations. */
  staffId: z.uuid().nullable(),
  /** Symptom description — populated on remote consultations. */
  reason: z.string().nullable(),
  /** Intercom conversation ID — present when sourced from Intercom. */
  intercomConversationId: z.string().nullable(),
  finalisedAt: isoDateTime.nullable(),
  finalisedBy: z.uuid().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Consultation = z.output<typeof consultationSchema>

/**
 * Derives the consultation status from its fields.
 *
 * - `pending`     — no staff member assigned (unassigned remote consultation)
 * - `in_progress` — consultation is active and editable
 * - `finalised`   — sealed as an immutable clinical record
 */
export function deriveConsultationStatus(c: {
  finalisedAt: string | null
  staffId: string | null
}): ConsultationStatus {
  if (c.finalisedAt) return 'finalised'
  if (!c.staffId) return 'pending'
  return 'in_progress'
}
